import { SuggestionsType } from '@analytics/const';
import { AnyQueryAnalyticsService, SearchAnalyticsService } from '@analytics/feature';
import { AsyncPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Optional,
  Output,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormControl } from '@angular/forms';
import { NavigationStart, Router } from '@angular/router';
import { CustomFacetContentType } from '@catalog/const';
import { SearchProductModeSwitcherService } from '@sdvor/layout/feature';
import { SdModal, SdModalService } from '@sdvor/shared/ui/v2';
import { CustomSearchBoxConfig, CustomSearchResults, SearchHistoryItem, Sts, Tap } from '@search/const';
import { TSearchBox } from '@search/const/v2';
import {
  CustomSearchBoxService,
  SearchCurrentQueryService,
  SearchFacetService,
  SearchHistoryService,
} from '@search/feature';
import { DeviceService, UrlNavigatorService } from '@shared/services';
import { IconTypes } from '@shared/v2';
import { combineLatest, Observable } from 'rxjs';
import { debounceTime, filter, map, take, tap, withLatestFrom } from 'rxjs/operators';
import { SdvorSearchboxComponent } from '../searchbox';

const DEFAULT_CONFIG: CustomSearchBoxConfig = {
  minCharactersBeforeRequest: 2,
  waitTimeBeforeRequest: 600,
  searchPhraseMaxLength: 255,
};

@Component({
  selector: 'sd-searchbox-container',
  templateUrl: './searchbox-container.component.html',
  styleUrls: ['./searchbox-container.component.scss'],
  imports: [AsyncPipe, SdvorSearchboxComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SdvorSearchBoxContainerComponent implements OnInit, OnDestroy {
  @Input() type: TSearchBox = 'default';
  @Input() placeholder = '';
  @Output() isExpandedChange = new EventEmitter<boolean>();
  @Output() isOpenedChange = new EventEmitter<boolean>();

  iconTypes = IconTypes;
  /**
   * Тип подсказки
   * Используется при отправке в аналитику
   */
  suggestionTypes = SuggestionsType;
  /**
   * Конфиг с настройками поиска, полученный с бекенда
   */
  config: CustomSearchBoxConfig = DEFAULT_CONFIG;
  /**
   * Результаты поиска
   */
  results$: Observable<CustomSearchResults>;
  query$: Observable<string>;
  /**
   * Статус загрузки подсказок
   */
  isLoading$: Observable<boolean>;
  /**
   * Возвращает тру, если результатов поиска нет
   */
  isEmpty$: Observable<boolean>;
  searchInputControl = new FormControl();

  /**
   * Получить форматированное значение поля ввода поисковой фразы
   */
  get searchInputValue(): string {
    return this.formatSearchPhrase(this.searchInputControl.value || '');
  }

  /**
   * Установить значение поля ввода поисковой фразы
   */
  set searchInputValue(value: string) {
    this.searchInputControl.patchValue(value);
  }

  private previousMode = false;
  private isTapSearch = false;
  private anyQueryAnalyticsData: { searchTerm?: string; correction?: string } = {};
  private isDesktop = toSignal(this.deviceService.isDesktopLgUp$, { initialValue: false });

  constructor(
    private customSearchBoxService: CustomSearchBoxService,
    public router: Router,
    public searchHistoryService: SearchHistoryService,
    private searchAnalyticsService: SearchAnalyticsService,
    private urlNavigatorService: UrlNavigatorService,
    private anyQueryAnalyticsService: AnyQueryAnalyticsService,
    private searchCurrentQueryService: SearchCurrentQueryService,
    private deviceService: DeviceService,
    private destroyRef: DestroyRef,
    private modalService: SdModalService,
    private searchProductModeSwitcher: SearchProductModeSwitcherService,
    @Optional() private sdModal: SdModal | null,
  ) {}

  ngOnInit(): void {
    this.isLoading$ = this.customSearchBoxService.getSuggestionLoading();
    this.results$ = this.customSearchBoxService.getResults();
    this.query$ = this.searchCurrentQueryService.query$;

    this.isEmpty$ = combineLatest({ isDesktop: this.deviceService.isDesktopLgUp$, results: this.results$ }).pipe(
      map(
        ({ isDesktop, results }) =>
          (isDesktop || !results.taps?.length) &&
          !results.sts?.length &&
          !results.categories?.length &&
          !results.searchHistory?.length,
      ),
    );

    // Сброс состояния нажатия подказки, когда стерли input
    this.searchInputControl.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((value: string) => {
      if (value.length === 0) {
        this.isTapSearch = false;
      }
    });

    /**
     * Отправка поискового запроса при вводе текста
     */
    this.searchInputControl.valueChanges
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap((value) => this.searchCurrentQueryService.setCurrentQuery(value)),
        debounceTime(this.config.waitTimeBeforeRequest || 0),
      )
      .subscribe(() => {
        this.loadSuggestions();
      });

    /**
     * Сброс результатов поиска при уходе со страницы поиска
     */
    this.router.events
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter(
          (event) =>
            event instanceof NavigationStart &&
            !event.url.match(/freeTextSearch=/) &&
            // если пользователя редиректнуло в результате поиска,
            // то поисковую фразу в серчбоксе не затираем
            !event.url.match(/searchRedirect=true/),
        ),
        withLatestFrom(this.isEmpty$),
        filter(([, isEmpty]) => !isEmpty),
      )
      .subscribe(() => {
        if (this.searchInputValue !== '') {
          this.searchInputValue = '';
          this.customSearchBoxService.clearResults();
        }
      });

    /**
     * Синхронизация инпута с поисковой строкой
     */
    this.customSearchBoxService
      .getCurrentQuery()
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter((query) => query !== this.searchInputValue),
      )
      .subscribe((query): void => {
        this.searchInputValue = query;
      });

    this.customSearchBoxService
      .getSuggestionResults()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((results) => {
        if (results?.query) {
          const searchTerm = results?.query;
          const correction = results?.correction ?? searchTerm;
          this.anyQueryAnalyticsData = { searchTerm, correction };
          this.anyQueryAnalyticsService.onLoadData({ searchTerm, correction });
        }
      });

    // временная тема, сделать нормально перед выкатом

    if (this.openedModalWindow()) {
      this.type = 'modal';
      this.placeholder = 'поиск';
    }

    if (this.openedModalWindow()) {
      this.previousMode = this.searchProductModeSwitcher.showSearchProduct();
      this.searchProductModeSwitcher.changeMode(false);
    }
  }

  ngOnDestroy(): void {
    if (this.openedModalWindow()) {
      this.searchProductModeSwitcher.changeMode(this.previousMode);
    }
  }

  openedModalWindow(): boolean {
    return Boolean(this.sdModal);
  }

  onSearchBoxClick(e: MouseEvent): void {
    if (this.isDesktop() || this.openedModalWindow() || this.modalService.hasOpenModals()) {
      return;
    }

    e.preventDefault();
    e.stopPropagation();

    this.modalService.open(SdvorSearchBoxContainerComponent, {
      modalType: 'fullscreen',
      hideCloseButton: true,
    });
  }

  onFocusSearchInput(): void {
    this.loadSuggestions();
  }

  onEnterSearchInput(): void {
    if (!this.searchInputValue || this.searchInputValue.length < (this.config.minCharactersBeforeRequest || 0)) {
      return;
    }

    this.results$
      .pipe(
        /**
         * Если очень быстро ввести поисковую фразу,
         * а затем нажать enter, то тут мы получим старые
         * результаты поиска, поэтому необходимо дождаться
         * результаты поиска с последней введенной фразой
         */
        filter((r) => r.freeTextSearch === this.searchInputValue),
        take(1),
      )
      .subscribe((results: CustomSearchResults) => {
        const freeTextSearch = results.freeTextSearch;
        const redirectUrl = results.redirect;

        if (this.isTapSearch) {
          this.sendToAnalytics(SuggestionsType.Tap, '');
          this.searchProducts(freeTextSearch || '', { redirectUrl, suggestionType: SuggestionsType.Tap });
        } else {
          this.searchAnalyticsService.onLaunchSearchResultWithoutSuggestion();
          this.searchProducts(freeTextSearch || '', { redirectUrl });
        }
      });
  }

  onClickTapItem(item: Tap): void {
    this.isTapSearch = true;
    this.searchInputValue = item.relatedSearch;
  }

  onClickStsItem(item: Sts): void {
    if (item.st) {
      this.sendToAnalytics(SuggestionsType.Sts, item.st);
      this.searchInputValue = item.st;
      this.searchProducts(item.st, { suggestionType: SuggestionsType.Sts, redirectUrl: item.redirectUrl });
    }
  }

  onClickHistoryItem(item: SearchHistoryItem): void {
    this.sendToAnalytics(SuggestionsType.History, item.text);
    this.searchInputValue = item.text;
    this.searchProducts(item.text, {
      suggestionType: SuggestionsType.History,
      redirectUrl: item.redirectUrl,
    });
  }

  onClickCategoryItem(item: { name: string; code: string }): void {
    this.sendToAnalytics(SuggestionsType.Category, item.name);
    this.searchCategory(item.name, item.code);
  }

  onClickBrandItem(item: { name: string; url: string }): void {
    this.sendToAnalytics(SuggestionsType.Brand, item.name);
    this.searchBrand(item.name);
  }

  /**
   * Очистить текстовое поле поиска
   */
  clear(): void {
    this.searchInputControl.patchValue('');
    this.loadSuggestions();
  }

  onSearchBoxOpened(opened: boolean): void {
    this.isOpenedChange.emit(opened);
  }

  private sendToAnalytics(type: SuggestionsType, value: string): void {
    this.searchAnalyticsService.onClickSuggestion({
      searchPhrase: this.searchInputValue || 'null',
      suggestionType: type,
      suggestionValue: value,
    });
    const { searchTerm, correction = '' } = this.anyQueryAnalyticsData;
    const item = value;
    if (typeof searchTerm === 'string') {
      switch (type) {
        case SuggestionsType.Sts:
          this.anyQueryAnalyticsService.onClickSts({ searchTerm, correction, item });
          break;
        case SuggestionsType.History:
          this.anyQueryAnalyticsService.onClickHistory({ searchTerm, correction, item });
          break;
        case SuggestionsType.Category:
          this.anyQueryAnalyticsService.onClickCategory({ searchTerm, correction, item });
          break;
        default:
          break;
      }
    }
  }

  /**
   * Поиск по продуктам
   */
  private searchProducts(
    text: string,
    {
      redirectUrl,
      suggestionType,
    }: {
      redirectUrl?: string;
      suggestionType?: SuggestionsType;
    } = {},
  ): void {
    this.searchHistoryService.push({ text, redirectUrl });
    this.customSearchBoxService.searchLaunch(text);

    if (redirectUrl) {
      this.urlNavigatorService.navigate(redirectUrl, {
        queryParams: { suggestionType, searchRedirect: true },
      });
    } else {
      this.customSearchBoxService.launchSearchPage({ suggestionType, freeTextSearch: text });
    }
  }

  /**
   * Поиск по товарам с зажатым фасетом по категории
   */
  private searchCategory(name: string, code: string): void {
    this.searchHistoryService.push({ text: this.searchInputValue });
    this.customSearchBoxService.searchLaunch(this.searchInputValue);

    const facets = SearchFacetService.createSearchQuery({
      activeFacets: [
        {
          contentType: CustomFacetContentType.Checkbox,
          name: 'Категория',
          code: 'categoryFacetPromo',
          value: { name, code },
        },
      ],
    });

    this.customSearchBoxService.launchSearchPage({
      facets,
      suggestionType: SuggestionsType.Category,
      freeTextSearch: this.searchInputValue,
    });
  }

  /**
   * Поиск по товарам с зажатым фасетом по бренду
   */
  private searchBrand(name: string): void {
    this.searchHistoryService.push({ text: this.searchInputValue });
    this.customSearchBoxService.searchLaunch(this.searchInputValue);

    const facets = SearchFacetService.createSearchQuery({
      activeFacets: [
        {
          contentType: CustomFacetContentType.Checkbox,
          name: 'Бренд',
          code: 'facet-brand',
          value: { name, code: name },
        },
      ],
    });

    this.customSearchBoxService.launchSearchPage({
      facets,
      suggestionType: SuggestionsType.Brand,
      freeTextSearch: this.searchInputValue,
    });
  }

  /**
   * Нормализация поисковой фразы
   */
  private formatSearchPhrase(value: string): string {
    return value.trim();
  }

  private loadSuggestions(): void {
    this.customSearchBoxService
      .getSuggestionResults()
      .pipe(take(1))
      .subscribe((result: CustomSearchResults) => {
        if (result?.freeTextSearch !== this.searchInputValue) {
          this.customSearchBoxService.search(this.searchInputValue, this.config);
        }
      });
  }
}
